import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunks with direct API calls
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryString.append(key, value);
        }
      });

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks?${queryString.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  'tasks/fetchTaskById',
  async (taskId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch task');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (taskData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(taskData),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create task');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ taskId, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update task');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Try to extract error message from response
        let errorMessage = 'Failed to delete task';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the response, use the default message
          console.error('Failed to parse error response:', parseError);
        }
        throw new Error(errorMessage);
      }

      return taskId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addTaskComment = createAsyncThunk(
  'tasks/addComment',
  async ({ taskId, comment }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}/comments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: comment }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add comment');
      }

      const data = await response.json();
      return { taskId, comment: data.data.comment };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTaskComment = createAsyncThunk(
  'tasks/updateComment',
  async ({ taskId, commentId, message }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}/comments/${commentId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update comment');
      }

      const data = await response.json();
      return { taskId, commentId, comment: data.data.comment };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTaskComment = createAsyncThunk(
  'tasks/deleteComment',
  async ({ taskId, commentId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      return { taskId, commentId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  tasks: [],
  currentTask: null,
  templates: [],
  stats: {
    totalTasks: 0,
    pendingTasks: 0,
    inProgressTasks: 0,
    completedTasks: 0,
    overdueTasks: 0
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  loading: false,
  error: null,
  filters: {
    status: '',
    priority: '',
    assignedTo: '',
    department: '',
    search: ''
  }
};

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    updateTaskInList: (state, action) => {
      const index = state.tasks.findIndex(task => task._id === action.payload._id);
      if (index !== -1) {
        state.tasks[index] = { ...state.tasks[index], ...action.payload };
      }
    },
    addTaskToList: (state, action) => {
      state.tasks.unshift(action.payload);
      state.stats.totalTasks += 1;
    },
    removeTaskFromList: (state, action) => {
      state.tasks = state.tasks.filter(task => task._id !== action.payload);
      state.stats.totalTasks = Math.max(0, state.stats.totalTasks - 1);
    },
    setCurrentTask: (state, action) => {
      state.currentTask = action.payload;
    },
    clearCurrentTask: (state) => {
      state.currentTask = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.data.tasks;
        state.stats = action.payload.data.stats;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single task
      .addCase(fetchTaskById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTask = action.payload.data.task;
      })
      .addCase(fetchTaskById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create task
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.unshift(action.payload.data.task);
        state.stats.totalTasks += 1;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update task
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task._id === action.payload.data.task._id);
        if (index !== -1) {
          state.tasks[index] = action.payload.data.task;
        }
        if (state.currentTask && state.currentTask._id === action.payload.data.task._id) {
          state.currentTask = action.payload.data.task;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete task
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter(task => task._id !== action.payload);
        state.stats.totalTasks = Math.max(0, state.stats.totalTasks - 1);
        if (state.currentTask && state.currentTask._id === action.payload) {
          state.currentTask = null;
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add comment
      .addCase(addTaskComment.fulfilled, (state, action) => {
        // Update current task if it's open
        if (state.currentTask && state.currentTask._id === action.payload.taskId) {
          state.currentTask.comments.push(action.payload.comment);
        }

        // Update the task in the main tasks list
        const taskIndex = state.tasks.findIndex(task => task._id === action.payload.taskId);
        if (taskIndex !== -1) {
          state.tasks[taskIndex].comments.push(action.payload.comment);
        }
      })

      // Update comment
      .addCase(updateTaskComment.fulfilled, (state, action) => {
        // Update current task if it's open
        if (state.currentTask && state.currentTask._id === action.payload.taskId) {
          const commentIndex = state.currentTask.comments.findIndex(c => c._id === action.payload.commentId);
          if (commentIndex !== -1) {
            state.currentTask.comments[commentIndex] = action.payload.comment;
          }
        }

        // Update the task in the main tasks list
        const taskIndex = state.tasks.findIndex(task => task._id === action.payload.taskId);
        if (taskIndex !== -1) {
          const commentIndex = state.tasks[taskIndex].comments.findIndex(c => c._id === action.payload.commentId);
          if (commentIndex !== -1) {
            state.tasks[taskIndex].comments[commentIndex] = action.payload.comment;
          }
        }
      })

      // Delete comment
      .addCase(deleteTaskComment.fulfilled, (state, action) => {
        // Update current task if it's open
        if (state.currentTask && state.currentTask._id === action.payload.taskId) {
          state.currentTask.comments = state.currentTask.comments.filter(c => c._id !== action.payload.commentId);
        }

        // Update the task in the main tasks list
        const taskIndex = state.tasks.findIndex(task => task._id === action.payload.taskId);
        if (taskIndex !== -1) {
          state.tasks[taskIndex].comments = state.tasks[taskIndex].comments.filter(c => c._id !== action.payload.commentId);
        }
      });
  }
});

export const {
  clearError,
  setFilters,
  clearFilters,
  updateTaskInList,
  addTaskToList,
  removeTaskFromList,
  setCurrentTask,
  clearCurrentTask
} = taskSlice.actions;

export default taskSlice.reducer;
